import { reactive } from 'vue';

/**
 * Reactive cache for the Tax Configuration pages. The live API
 * (`services/taxConfigApi.js`) is the source of truth; this store holds the
 * mapped, UI-shaped copy that the views/dialogs render. `taxConfigSeed.js`
 * remains only as an optional dev fallback (in-memory) for offline mockups.
 *
 * UI rule shape is snake_case (rule_code, cap_value, calc_base, ...) to
 * match the existing dialog/editor components; API DTOs are camelCase
 * (ruleCode, capValue, calcBase, ...). The mappers below bridge the two.
 *
 * @typedef {Object} UiTaxRule
 * @property {number} [rule_id] @property {number} [version_id]
 * @property {string} rule_code @property {string} category @property {string} category_label
 * @property {string} label @property {?string} type
 * @property {number} sort_order @property {boolean} active
 */

const state = reactive({
  years: [],
});

function pad2(value) {
  return String(value).padStart(2, '0');
}

function formatDateDDMMYYYY(date) {
  return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
}

function formatYMD(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  if (!y || !m || !d) return ymd;
  return `${d}/${m}/${y}`;
}

function normalizeVersionCode(value) {
  return String(value || '').trim().toUpperCase().replace(/[^A-Z0-9._-]/g, '');
}

function getYears() {
  return state.years.slice().sort((a, b) => b.year - a.year);
}

function getYear(taxYear) {
  const matches = state.years.filter((row) => row.taxYear === taxYear);
  if (!matches.length) return null;
  // Prefer a persisted record (carries yearID) over any in-memory/seed copy.
  return matches.find((row) => row.yearID != null) || matches[0];
}

function getVersions(taxYear) {
  const year = getYear(taxYear);
  if (!year) return [];
  return year.versions.slice().sort((a, b) => a.versionCode.localeCompare(b.versionCode, undefined, { numeric: true }));
}

function getVersion(taxYear, versionCode) {
  const year = getYear(taxYear);
  if (!year) return null;
  const matches = (year.versions || []).filter((row) => row.versionCode === versionCode);
  if (!matches.length) return null;
  // Prefer a persisted record (carries versionID) over any in-memory/seed copy
  // so the editor always gets a saveable version (SAVE stays enabled).
  return matches.find((row) => row.versionID != null) || matches[0];
}

function upsertYear(payload) {
  const taxYear = String(payload.taxYear).trim().toUpperCase();
  const existing = getYear(taxYear);
  if (existing) {
    Object.assign(existing, {
      year: payload.year ?? existing.year,
      description: payload.description ?? existing.description,
      effectiveStartDate: payload.effectiveStartDate ?? existing.effectiveStartDate,
      effectiveEndDate: payload.effectiveEndDate ?? existing.effectiveEndDate,
      isActive: payload.isActive ?? existing.isActive,
      updatedDate: formatDateDDMMYYYY(new Date()),
    });
    return existing;
  }
  const next = {
    taxYear,
    year: payload.year,
    description: payload.description || '',
    effectiveStartDate: payload.effectiveStartDate || '',
    effectiveEndDate: payload.effectiveEndDate || '',
    isActive: Boolean(payload.isActive),
    updatedDate: formatDateDDMMYYYY(new Date()),
    versions: [],
  };
  state.years.push(next);
  return next;
}

function setYearActive(taxYear, isActive) {
  const year = getYear(taxYear);
  if (year) {
    year.isActive = isActive;
    year.updatedDate = formatDateDDMMYYYY(new Date());
  }
}

function deleteYear(taxYear) {
  const idx = state.years.findIndex((row) => row.taxYear === taxYear);
  if (idx >= 0) state.years.splice(idx, 1);
}

function upsertVersion(payload) {
  const taxYear = String(payload.taxYear).trim().toUpperCase();
  const versionCode = normalizeVersionCode(payload.versionCode);
  if (!versionCode) return null;

  let year = getYear(taxYear);
  if (!year) {
    year = upsertYear({
      taxYear,
      year: payload.year,
      description: payload.yearDescription || '',
      effectiveStartDate: payload.effectiveStartDate || '',
      effectiveEndDate: payload.effectiveEndDate || '',
      isActive: false,
    });
  }

  let version = year.versions.find((row) => row.versionCode === versionCode);
  if (version) {
    Object.assign(version, {
      description: payload.description ?? version.description,
      effectiveStartDate: payload.effectiveStartDate ?? version.effectiveStartDate,
      effectiveEndDate: payload.effectiveEndDate ?? version.effectiveEndDate,
      isActive: payload.isActive ?? version.isActive,
      updatedDate: formatDateDDMMYYYY(new Date()),
    });
    return version;
  }

  version = {
    taxYear,
    year: year.year,
    versionCode,
    description: payload.description || `Tax configuration ${versionCode}`,
    effectiveStartDate: payload.effectiveStartDate || '',
    effectiveEndDate: payload.effectiveEndDate || '',
    isActive: Boolean(payload.isActive),
    updatedDate: formatDateDDMMYYYY(new Date()),
    rules: [],
  };
  year.versions.push(version);
  return version;
}

function setVersionActive(taxYear, versionCode, isActive) {
  const version = getVersion(taxYear, versionCode);
  if (version) {
    version.isActive = isActive;
    version.updatedDate = formatDateDDMMYYYY(new Date());
  }
}

function deleteVersion(taxYear, versionCode) {
  const year = getYear(taxYear);
  if (!year) return;
  const idx = year.versions.findIndex((row) => row.versionCode === versionCode);
  if (idx >= 0) year.versions.splice(idx, 1);
}

function getRules(taxYear, versionCode) {
  const version = getVersion(taxYear, versionCode);
  return version ? version.rules : [];
}

function upsertRule(taxYear, versionCode, rule) {
  const version = getVersion(taxYear, versionCode);
  if (!version) return null;
  const idx = version.rules.findIndex((row) => row.rule_code === rule.rule_code);
  if (idx >= 0) {
    version.rules.splice(idx, 1, { ...version.rules[idx], ...rule });
  } else {
    version.rules.push({ ...rule });
  }
  version.updatedDate = formatDateDDMMYYYY(new Date());
  return rule;
}

function deleteRule(taxYear, versionCode, ruleCode) {
  const version = getVersion(taxYear, versionCode);
  if (!version) return;
  const idx = version.rules.findIndex((row) => row.rule_code === ruleCode);
  if (idx >= 0) {
    version.rules.splice(idx, 1);
    version.updatedDate = formatDateDDMMYYYY(new Date());
  }
}

function setRuleActive(taxYear, versionCode, ruleCode, isActive) {
  const version = getVersion(taxYear, versionCode);
  if (!version) return;
  const rule = version.rules.find((row) => row.rule_code === ruleCode);
  if (rule) {
    rule.active = isActive;
    version.updatedDate = formatDateDDMMYYYY(new Date());
  }
}

function importVersion(taxYear, versionCode, rules) {
  const version = getVersion(taxYear, versionCode);
  if (!version) return;
  version.rules = Array.isArray(rules) ? rules.map((rule) => ({ ...rule })) : [];
  version.updatedDate = formatDateDDMMYYYY(new Date());
}

function loadYearsFromApi(apiYears) {
  if (!Array.isArray(apiYears)) return;
  const incomingCodes = new Set();

  apiYears.forEach((dto) => {
    const taxYear = String(dto.taxYearCode || '').trim().toUpperCase();
    if (!taxYear) return;
    incomingCodes.add(taxYear);

    const existing = getYear(taxYear);
    const mapped = {
      yearID: dto.yearID,
      taxYear,
      year: dto.year,
      beYear: dto.beYear,
      description: dto.description || '',
      effectiveStartDate: (dto.effectiveStartDate || '').slice(0, 10),
      effectiveEndDate: (dto.effectiveEndDate || '').slice(0, 10),
      isActive: Boolean(dto.isActive),
      updatedDate: dto.updateDate
        ? formatDateDDMMYYYY(new Date(dto.updateDate))
        : (dto.createDate ? formatDateDDMMYYYY(new Date(dto.createDate)) : ''),
    };

    if (existing) {
      Object.assign(existing, mapped);
    } else {
      state.years.push({ ...mapped, versions: [] });
    }
  });

  for (let i = state.years.length - 1; i >= 0; i -= 1) {
    const row = state.years[i];
    if (row.yearID != null && !incomingCodes.has(row.taxYear)) {
      state.years.splice(i, 1);
    }
  }
}

function loadVersionsFromApi(apiVersions) {
  if (!Array.isArray(apiVersions)) return;

  const byYearId = new Map();
  state.years.forEach((y) => {
    if (y.yearID != null) byYearId.set(y.yearID, y);
    y.versions = (y.versions || []).filter((v) => v.versionID == null);
  });

  apiVersions.forEach((dto) => {
    const year = byYearId.get(dto.yearID);
    if (!year) return;
    year.versions.push(mapVersionDto(dto, year));
  });
}

function mapVersionDto(dto, year) {
  return {
    versionID: dto.versionID,
    yearID: dto.yearID,
    taxYear: year.taxYear,
    year: year.year,
    versionCode: dto.versionCode,
    description: dto.description || '',
    effectiveStartDate: (dto.effectiveStartDate || '').slice(0, 10),
    effectiveEndDate: (dto.effectiveEndDate || '').slice(0, 10),
    isActive: Boolean(dto.isActive),
    updatedDate: dto.updateDate
      ? formatDateDDMMYYYY(new Date(dto.updateDate))
      : (dto.createDate ? formatDateDDMMYYYY(new Date(dto.createDate)) : ''),
    rules: [],
  };
}

function upsertVersionFromApi(dto) {
  const year = state.years.find((y) => y.yearID === dto.yearID);
  if (!year) return null;
  const mapped = mapVersionDto(dto, year);
  const idx = year.versions.findIndex((v) => v.versionID === dto.versionID);
  if (idx >= 0) {
    year.versions.splice(idx, 1, { ...year.versions[idx], ...mapped });
    return year.versions[idx];
  }
  year.versions.push(mapped);
  return mapped;
}

function setVersionActiveLocal(versionID, isActive) {
  for (const year of state.years) {
    const v = (year.versions || []).find((row) => row.versionID === versionID);
    if (v) {
      v.isActive = isActive;
      v.updatedDate = formatDateDDMMYYYY(new Date());
      return;
    }
  }
}

function deleteVersionByID(versionID) {
  for (const year of state.years) {
    const idx = (year.versions || []).findIndex((row) => row.versionID === versionID);
    if (idx >= 0) {
      year.versions.splice(idx, 1);
      return;
    }
  }
}

function seedIfEmpty(data) {
  if (!data || state.years.length > 0) return;

  const yearMap = new Map();

  (data.year || []).forEach((entry) => {
    const taxYear = String(entry.tax_year || '').trim().toUpperCase();
    if (!taxYear) return;

    if (!yearMap.has(taxYear)) {
      yearMap.set(taxYear, {
        taxYear,
        year: entry.year,
        description: entry.description || '',
        effectiveStartDate: entry.effective_start_date || '',
        effectiveEndDate: entry.effective_end_date || '',
        isActive: Boolean(entry.is_active),
        updatedDate: entry.updated_date || formatDateDDMMYYYY(new Date()),
        versions: [],
      });
    }

    const yearEntry = yearMap.get(taxYear);
    const versionCode = normalizeVersionCode(entry.version_code || 'V1');

    const rules = [];
    (entry.category || []).forEach((cat) => {
      (cat.tax_rule || []).forEach((rule) => {
        rules.push({
          ...rule,
          category: rule.category || cat.category_code || '',
          category_label: rule.category_label || cat.category_label || '',
        });
      });
    });

    yearEntry.versions.push({
      taxYear,
      year: entry.year,
      versionCode,
      description: entry.description || `Tax configuration ${versionCode}`,
      effectiveStartDate: entry.effective_start_date || '',
      effectiveEndDate: entry.effective_end_date || '',
      isActive: Boolean(entry.is_active),
      updatedDate: entry.updated_date || formatDateDDMMYYYY(new Date()),
      rules,
    });
  });

  yearMap.forEach((yearData) => {
    state.years.push(yearData);
  });
}

// ── Rule mapping (API camelCase DTO ⇄ UI snake_case shape) ───────────────

function sliceDate(value) {
  return value ? String(value).slice(0, 10) : null;
}

/**
 * Map a TaxConfigRuleDto (camelCase) → UI rule (snake_case).
 * @param {Object} dto
 * @param {string} [categoryLabel] resolved from the category group when available
 * @returns {UiTaxRule}
 */
function mapRuleDtoToUi(dto, categoryLabel) {
  return {
    rule_id: dto.ruleID,
    version_id: dto.versionID,
    rule_code: dto.ruleCode || '',
    category: dto.categoryCode || '',
    category_label: categoryLabel || dto.categoryLabel || '',
    label: dto.label || '',
    type: dto.type ?? 'number',
    sort_order: dto.sortOrder ?? 0,
    active: dto.isActive !== false,
  };
}

/**
 * Map a UI rule (snake_case) → CreateTaxConfigRuleRequest (camelCase) for
 * create / update / bulk-save.
 * @param {UiTaxRule} rule
 * @param {number} versionID
 * @returns {Object}
 */
function buildRuleRequest(rule, versionID) {
  return {
    versionID,
    categoryCode: rule.category || '',
    ruleCode: rule.rule_code || '',
    label: rule.label || rule.rule_code || '',
    type: rule.type,
    sortOrder: Number(rule.sort_order) || 0,
    isActive: rule.active !== false,
  };
}

/**
 * Load a `/full` config payload into the cached version's rule list.
 * Flattens categories → rules (preserving category sort order + rule order)
 * and fills category_label from the master.
 * @returns {UiTaxRule[]} the flattened, mapped rule list
 */
function loadFullConfigIntoVersion(taxYear, versionCode, fullDto) {
  const version = getVersion(taxYear, versionCode);
  if (!version) return [];
  const categories = (fullDto && fullDto.categories) || [];
  const rules = [];
  categories.forEach((cat) => {
    (cat.rules || []).forEach((dto) => {
      rules.push(mapRuleDtoToUi(dto, cat.categoryLabel));
    });
  });
  version.rules = rules;
  return rules;
}

export const taxConfigStore = {
  state,
  getYears,
  getYear,
  getVersions,
  getVersion,
  upsertYear,
  setYearActive,
  deleteYear,
  upsertVersion,
  setVersionActive,
  deleteVersion,
  getRules,
  upsertRule,
  deleteRule,
  setRuleActive,
  importVersion,
  seedIfEmpty,
  loadYearsFromApi,
  loadVersionsFromApi,
  upsertVersionFromApi,
  setVersionActiveLocal,
  deleteVersionByID,
  mapRuleDtoToUi,
  buildRuleRequest,
  loadFullConfigIntoVersion,
  formatDateDDMMYYYY,
  formatYMD,
  normalizeVersionCode,
};

export default taxConfigStore;
